import { getDevicePPI } from "./PDFButtons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectPageSize, applyLoadedPDFTemplate, setData } from "../PDFSlice"
import { ButtonSecondary, ButtonBorderless } from "@nova/component-btn"
import { useCallback } from "react"
import { PartialViewState } from "@nova/constants"
import { createPDFTemplate } from "features/entity-caching/fires/thunks"
import { selectActivatedFireId } from "features/map/control/selectors"
import { zoomToArea } from "features/map/control/slice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useSWRNoRevalidate } from "@nova/swr"
import { mutate } from "swr"
import store from "integrations/redux/store"
import { useAsyncCallback } from "@nova/use-async-callback"
import { getViewport } from "features/map/GlobalViewState"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"

export function PDFTemplates() {
    const dispatch = useAppDispatch()

    const projectId = useAppSelector(selectActivatedFireId)
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const size = useAppSelector(selectPageSize())
    const { data, isLoading } = useSWRNoRevalidate(`/api/organization/${workspaceId}/pdfTemplate`)
    const { saveTemplate, loading: saveTemplateLoading } = useAsyncCallback(
        {
            saveTemplate: async () => {
                try {
                    const { pdfTemplate: template } = await createPDFTemplate(store.getState())
                    await mutate(
                        `/api/organization/${workspaceId}/pdfTemplate`,
                        async () => {
                            const res = await fetch(
                                `/api/organization/${workspaceId}/pdfTemplate`,
                                {
                                    method: "POST",
                                    headers: { "content-type": "application/json" },
                                    body: JSON.stringify({ template }),
                                }
                            )
                            if (!res.ok) throw new Error("update failed")

                            const data = await res.json()
                            return { template: JSON.parse(data.result.template) }
                        },
                        {
                            optimisticData: template,
                            rollbackOnError: true,
                            revalidate: false,
                            populateCache: true,
                        }
                    )
                    dispatch(
                        setGlobalNotificationContext({
                            message: "Defaults saved",
                            timeoutInMs: 3000,
                            status: GlobalNotificationStatus.success,
                        })
                    )
                } catch (err) {
                    console.error(err)
                    dispatch(
                        setGlobalNotificationContext({
                            message: "Error saving defaults",
                            timeoutInMs: 3000,
                            status: GlobalNotificationStatus.error,
                        })
                    )
                }
            },
        },
        [workspaceId]
    )

    const calculateZoomLevel = useCallback(
        (ratio: string): number | undefined => {
            const num = parseFloat(ratio) || 0
            const snapshotEl = document.getElementById("pdf-snapshot")
            dispatch(setData({ type: "ratio", data: ratio })) // maybe redundant
            if (!snapshotEl || num <= 0) return
            const zoom = Math.log2(
                (getDevicePPI() *
                    (snapshotEl.getBoundingClientRect().height /
                        getDevicePPI() /
                        parseFloat(size.split('"')[0])) *
                    (1 / 0.0254) *
                    (78271.51696 * Math.cos((getViewport().latitude * Math.PI) / 180))) /
                    num
            )
            return Math.max(4, Math.min(zoom, 24))
        },
        [size, dispatch]
    )

    const handleLoadTemplateClicked = useCallback(() => {
        if (data && data.template && workspaceId && projectId) {
            const template = data.template
            dispatch(
                applyLoadedPDFTemplate({
                    template,
                    workspaceId: workspaceId,
                })
            )

            // Move map to coordinates if template included them.
            // Set zoom/ratio if template included them.
            // This might seem redundant but if a literal undefined value is
            // passed to calculateZoomLevel, the app crashes.
            if (template.ratio) {
                const area: Partial<PartialViewState> = {}

                area.zoom = calculateZoomLevel(template.ratio)

                dispatch(zoomToArea(area))
            }

            dispatch(
                setGlobalNotificationContext({
                    message: "Loaded defaults",
                    timeoutInMs: 3000,
                    status: GlobalNotificationStatus.success,
                })
            )
        } else {
            dispatch(
                setGlobalNotificationContext({
                    message: "Error loading defaults",
                    timeoutInMs: 3000,
                    status: GlobalNotificationStatus.error,
                })
            )
        }
    }, [calculateZoomLevel, workspaceId, projectId, dispatch, data])

    return (
        <div className="flex flex-col gap-2 mt-2">
            <ButtonSecondary
                title="Save as Default"
                onClick={saveTemplate}
                disabled={saveTemplateLoading}
            />
            <ButtonBorderless
                title="Reset to Default"
                disabled={isLoading || !data?.template}
                onClick={handleLoadTemplateClicked}
            />
        </div>
    )
}
